import {trace} from '@opentelemetry/api';
import * as webVitals from 'web-vitals';
import {SpanStatusCode} from '@opentelemetry/api';

const RESOURCE_BATCH_INTERVAL = 2000; // 2 seconds
const RESOURCE_BATCH_SIZE = 10; // Process resources in batches
const MAX_RESOURCE_ENTRIES = 100; // Cap maximum number of resource entries

// List of resource types to track by default
// Exclude CSS, fonts and other less important resources that generate noise
const TRACKED_RESOURCE_TYPES = ['script', 'xmlhttprequest', 'fetch', 'beacon', 'audio', 'video'];

// Resource size threshold - only track resources larger than this (in bytes)
const RESOURCE_SIZE_THRESHOLD = 50000; // 50KB

// Define the global error tracker type
interface ErrorTrackerEntry {
  type: string;
  message: string;
  stack?: string;
  timestamp: string;
  url: string;
  traceId?: string;
  component?: string;
  handled?: boolean;
}

interface ErrorTracker {
  notificationErrors: ErrorTrackerEntry[];
  errorBoundaries: ErrorTrackerEntry[];
  axiosErrors: ErrorTrackerEntry[];
  duplicateErrorBoundaries?: Record<string, any[]>;
}

// Interface for tracking fetch errors that have been handled
interface FetchErrorInfo {
  timestamp: number;
  url: string;
  status: number;
  statusText: string;
}

// Extend the Window interface
declare global {
  interface Window {
    __errorTracker?: ErrorTracker;
    __fetchErrorsHandled?: Record<string, FetchErrorInfo>;
    __recentErrorNotifications?: Record<string, number>;
  }
}

// Create a dedicated tracer for performance metrics
const getPerformanceTracer = () => {
  return trace.getTracer('performance-metrics');
};

// Helper to check if a resource should be tracked
const shouldTrackResource = (resourceEntry: PerformanceResourceTiming): boolean => {
  // Skip small resources
  if (resourceEntry.transferSize < RESOURCE_SIZE_THRESHOLD) {
    return false;
  }

  // Skip resource types we're not interested in
  if (!TRACKED_RESOURCE_TYPES.includes(resourceEntry.initiatorType)) {
    // Exception: track very slow resources regardless of type if they exceed a threshold
    if (resourceEntry.duration > 1000) { // 1 second
      return true;
    }
    return false;
  }

  return true;
};

/**
 * Throttle function to prevent excessive calls
 * @param {Function} func - The function to throttle
 * @param {number} limit - Time limit in milliseconds between function calls
 * @return {Function} Throttled function that will only execute once within the time limit
 */
function throttle<T extends(...args: any[]) => any>(
  func: T,
  limit: number,
): (...args: Parameters<T>) => ReturnType<T> | undefined {
  let inThrottle = false;
  let lastResult: ReturnType<T> | undefined;

  return function(...args: Parameters<T>): ReturnType<T> | undefined {
    if (!inThrottle) {
      lastResult = func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
    return lastResult;
  };
}

// Helper to normalize resource URL to avoid duplicates
function normalizeResourceUrl(url: string): string {
  try {
    // Remove query params and hashes
    const parsedUrl = new URL(url);
    return `${parsedUrl.origin}${parsedUrl.pathname}`;
  } catch (e) {
    return url; // If parsing fails, return the original URL
  }
}

/**
 * Initialize performance monitoring with web-vitals
 */
export const initializePerformanceMonitoring = () => {
  // Skip on server
  if (typeof window === 'undefined') return;

  // Monitor Core Web Vitals
  webVitals.onCLS(({value}) => {
    const tracer = getPerformanceTracer();
    const span = tracer.startSpan('web_vital_cls');
    span.setAttribute('frontend.cumulative_layout_shift', value);
    span.end();
  });

  webVitals.onFID(({value}) => {
    const tracer = getPerformanceTracer();
    const span = tracer.startSpan('web_vital_fid');
    span.setAttribute('frontend.first_input_delay_ms', value);
    span.end();
  });

  webVitals.onLCP(({value}) => {
    const tracer = getPerformanceTracer();
    const span = tracer.startSpan('web_vital_lcp');
    span.setAttribute('frontend.largest_contentful_paint_ms', value);
    span.end();
  });

  webVitals.onFCP(({value}) => {
    const tracer = getPerformanceTracer();
    const span = tracer.startSpan('web_vital_fcp');
    span.setAttribute('frontend.first_contentful_paint_ms', value);
    span.end();
  });

  webVitals.onTTFB(({value}) => {
    const tracer = getPerformanceTracer();
    const span = tracer.startSpan('web_vital_ttfb');
    span.setAttribute('frontend.time_to_first_byte_ms', value);
    span.end();
  });

  // Monitor resource timing metrics
  monitorResourceTiming();

  // Setup long task observer
  monitorLongTasks();
};

/**
 * Monitor resource timing entries (images, scripts, etc)
 * @return {Function} A cleanup function that disconnects the observer when called
 */
const monitorResourceTiming = () => {
  // Skip already processed entries to prevent duplicates
  const processedEntries = new Set<string>();

  // Queue for batching resource entries
  const resourceQueue: PerformanceResourceTiming[] = [];
  let processingInterval: number | null = null;

  // Counter to limit the total number of entries processed
  let entriesProcessed = 0;

  // Process a batch of resource entries
  const processBatch = () => {
    if (resourceQueue.length === 0) {
      if (processingInterval) {
        clearInterval(processingInterval);
        processingInterval = null;
      }
      return;
    }

    const tracer = getPerformanceTracer();
    const batch = resourceQueue.splice(0, RESOURCE_BATCH_SIZE);

    // Group by resource type for more efficient reporting
    const resourcesByType: Record<string, PerformanceResourceTiming[]> = {};

    batch.forEach(entry => {
      const type = entry.initiatorType;
      if (!resourcesByType[type]) {
        resourcesByType[type] = [];
      }
      resourcesByType[type].push(entry);
    });

    // Create one span per resource type in this batch
    Object.entries(resourcesByType).forEach(([type, entries]) => {
      const span = tracer.startSpan(`resource_batch_${type}`);

      span.setAttribute('frontend.resource_type', type);
      span.setAttribute('frontend.resource_count', entries.length);

      // Calculate aggregate metrics
      let totalDuration = 0;
      let maxDuration = 0;
      let totalSize = 0;
      let slowResourcesCount = 0;

      entries.forEach(entry => {
        totalDuration += entry.duration;
        maxDuration = Math.max(maxDuration, entry.duration);
        totalSize += entry.transferSize || 0;

        if (entry.duration > 1000) {
          slowResourcesCount++;
        }
      });

      // Add aggregate attributes
      span.setAttribute('frontend.resource_total_duration_ms', totalDuration);
      span.setAttribute('frontend.resource_avg_duration_ms', totalDuration / entries.length);
      span.setAttribute('frontend.resource_max_duration_ms', maxDuration);
      span.setAttribute('frontend.resource_total_size_bytes', totalSize);
      span.setAttribute('frontend.slow_resources_count', slowResourcesCount);

      // For debugging, include details of the slowest resources (up to 3)
      const slowestResources = entries
        .slice()
        .sort((a, b) => b.duration - a.duration)
        .slice(0, 3);

      slowestResources.forEach((resource, idx) => {
        span.setAttribute(`frontend.slow_resource_${idx}_url`, resource.name);
        span.setAttribute(`frontend.slow_resource_${idx}_duration_ms`, resource.duration);
      });

      span.end();
    });
  };

  // Throttled handler for resource observation to prevent excessive processing
  const handleResourceEntries = throttle((entries: PerformanceResourceTiming[]) => {
    entries.forEach(resourceEntry => {
      // Skip if already processed or if we've reached our limit
      const resourceKey = normalizeResourceUrl(resourceEntry.name);
      if (processedEntries.has(resourceKey) || entriesProcessed >= MAX_RESOURCE_ENTRIES) return;

      // Skip API calls (those are already instrumented)
      if (resourceEntry.initiatorType === 'xmlhttprequest' || resourceEntry.initiatorType === 'fetch') {
        return;
      }

      // Apply filtering to reduce noise
      if (!shouldTrackResource(resourceEntry)) {
        return;
      }

      // Add to set and increment counter
      processedEntries.add(resourceKey);
      entriesProcessed++;

      // Add to processing queue
      resourceQueue.push(resourceEntry);

      // Start interval processing if not already running
      if (!processingInterval && resourceQueue.length >= RESOURCE_BATCH_SIZE) {
        processBatch();
        processingInterval = window.setInterval(processBatch, RESOURCE_BATCH_INTERVAL);
      }
    });
  }, 100); // Throttle to once every 100ms

  // Observer for resource timing entries
  const resourceObserver = new PerformanceObserver(list => {
    handleResourceEntries(list.getEntries() as PerformanceResourceTiming[]);
  });

  // Start observing resource timing entries
  resourceObserver.observe({
    type: 'resource',
    buffered: true,
  });

  // Start the interval immediately to handle initial entries
  processingInterval = window.setInterval(processBatch, RESOURCE_BATCH_INTERVAL);

  // Return a cleanup function
  return () => {
    resourceObserver.disconnect();
    if (processingInterval) {
      clearInterval(processingInterval);
    }
  };
};

/**
 * Monitor long tasks on the main thread
 * @return {Function} A cleanup function that disconnects the observer when called
 */
const monitorLongTasks = () => {
  // Count of long tasks
  let longTasksCount = 0;
  let totalLongTaskTime = 0;
  let lastReportTime = Date.now();
  const REPORTING_INTERVAL = 10000; // 10 seconds

  // Observer for long tasks
  const longTaskObserver = new PerformanceObserver(list => {
    const tracer = getPerformanceTracer();
    const entries = list.getEntries();

    entries.forEach(entry => {
      longTasksCount++;
      totalLongTaskTime += entry.duration;
    });

    // Only report at intervals to reduce number of spans
    const now = Date.now();
    if (now - lastReportTime >= REPORTING_INTERVAL) {
      // Create a span for aggregated long tasks
      const span = tracer.startSpan('long_tasks_aggregate');
      span.setAttribute('frontend.long_tasks_count', longTasksCount);
      span.setAttribute('frontend.long_tasks_total_duration_ms', totalLongTaskTime);
      span.setAttribute('frontend.long_tasks_reporting_interval_ms', REPORTING_INTERVAL);
      span.setAttribute('frontend.long_tasks_avg_duration_ms', totalLongTaskTime / Math.max(1, longTasksCount));
      span.end();

      lastReportTime = now;
    }
  });

  // Start observing long tasks
  try {
    longTaskObserver.observe({
      type: 'longtask',
      buffered: true,
    });
    return () => longTaskObserver.disconnect();
  } catch (e) {
    // Some browsers don't support longtask observation
    console.debug('[otel-performance] Long tasks not supported in this browser');
    return () => {};
  }
};

/**
 * Component render timing - to be used with React components
 * @param {string} componentName The name of the component being measured
 * @return {Function} A function to be called when the component has finished rendering
 */
export const measureComponentRender = (componentName: string) => {
  const startTime = performance.now();

  // Keep track of render times for throttling reports
  const renderTimes: Record<string, {
    count: number;
    totalTime: number;
    lastReported: number;
  }> = {};

  if (!renderTimes[componentName]) {
    renderTimes[componentName] = {
      count: 0,
      totalTime: 0,
      lastReported: 0,
    };
  }

  return () => {
    const endTime = performance.now();
    const duration = endTime - startTime;

    // Update render stats for this component
    renderTimes[componentName].count++;
    renderTimes[componentName].totalTime += duration;

    // Only report if this is a significant render or it's been a while
    const now = Date.now();
    const timeSinceLastReport = now - renderTimes[componentName].lastReported;
    const isSlowRender = duration > 50; // 50ms threshold for "slow" render

    if (isSlowRender || timeSinceLastReport > 10000) { // Report at most every 10 seconds
      const tracer = getPerformanceTracer();
      const span = tracer.startSpan('component_render');

      span.setAttribute('component.name', componentName);
      span.setAttribute('frontend.render_duration_ms', duration);
      span.setAttribute('frontend.is_slow_render', isSlowRender);

      // Add aggregate stats if we've collected multiple renders
      if (renderTimes[componentName].count > 1) {
        span.setAttribute('frontend.render_count', renderTimes[componentName].count);
        span.setAttribute('frontend.render_avg_duration_ms',
          renderTimes[componentName].totalTime / renderTimes[componentName].count);
      }

      span.end();

      // Reset stats after reporting
      renderTimes[componentName] = {
        count: 0,
        totalTime: 0,
        lastReported: now,
      };
    }
  };
};

/**
 * Error tracking setup
 * @return {Function|undefined} A cleanup function if running in browser environment
 */
export const initializeErrorTracking = () => {
  // Skip on server
  if (typeof window === 'undefined') return;

  // Create a global tracker for error sources
  window.__errorTracker = {
    notificationErrors: [],
    errorBoundaries: [],
    axiosErrors: [],
  };

  // Deduplication of errors to prevent spam
  const seenErrors = new Set<string>();
  const getErrorKey = (error: any): string => {
    if (!error) return 'unknown';
    return `${error.name}:${error.message}:${error.filename || ''}:${error.lineno || ''}`;
  };

  // Global error handler
  window.addEventListener('error', event => {
    const errorKey = getErrorKey(event.error || event);

    // Skip if we've seen this exact error recently
    if (seenErrors.has(errorKey)) return;
    seenErrors.add(errorKey);

    // Track this error globally
    if (window.__errorTracker) {
      window.__errorTracker.axiosErrors.push({
        type: 'unhandled_error',
        message: event.error?.message || event.message,
        stack: event.error?.stack,
        timestamp: new Date().toISOString(),
        url: window.location.href,
      });
    }

    // Clean up set periodically to allow reporting if the same error happens later
    setTimeout(() => seenErrors.delete(errorKey), 60000);

    const tracer = trace.getTracer('error-handler');
    const span = tracer.startSpan('unhandled_error');

    span.setAttribute('error.type', event.error?.name || 'Error');
    span.setAttribute('error.message', event.error?.message || event.message);
    span.setAttribute('error.stack', event.error?.stack || '');
    span.setAttribute('error.filename', event.filename);
    span.setAttribute('error.line_number', event.lineno);
    span.setAttribute('error.column_number', event.colno);
    span.setAttribute('error.handled', false);

    // Add device and environment context
    span.setAttribute('page.url', window.location.href);
    span.setAttribute('environment', process.env.NEXT_PUBLIC_APP_ENV || 'unknown');

    // Explicitly set the span status to ERROR
    span.setStatus({
      code: SpanStatusCode.ERROR,
      message: event.error?.message || event.message || 'Unhandled error',
    });

    // Record as exception
    if (event.error) {
      span.recordException(event.error);
    }

    span.end();
  });

  // Promise rejection handler with deduplication
  window.addEventListener('unhandledrejection', event => {
    const errorKey = getErrorKey(event.reason);

    // Skip if we've seen this exact rejection recently
    if (seenErrors.has(errorKey)) return;
    seenErrors.add(errorKey);

    // Clean up set periodically
    setTimeout(() => seenErrors.delete(errorKey), 60000);

    const tracer = trace.getTracer('error-handler');
    const span = tracer.startSpan('unhandled_promise_rejection');

    span.setAttribute('error.type', 'UnhandledPromiseRejection');
    span.setAttribute('error.message',
      event.reason?.message || String(event.reason) || 'Unknown promise rejection');
    span.setAttribute('error.stack', event.reason?.stack || '');
    span.setAttribute('error.handled', false);

    // Add device and environment context
    span.setAttribute('page.url', window.location.href);
    span.setAttribute('environment', process.env.NEXT_PUBLIC_APP_ENV || 'unknown');

    // Explicitly set the span status to ERROR
    span.setStatus({
      code: SpanStatusCode.ERROR,
      message: event.reason?.message || String(event.reason) || 'Unhandled promise rejection',
    });

    // Record as exception if it's an Error object
    if (event.reason instanceof Error) {
      span.recordException(event.reason);
    }

    span.end();
  });

  // Return cleanup function
  return () => {
    seenErrors.clear();
  };
};
